// El brief mínimo de V360, editable. Lo usan el formulario, la propuesta de la IA
// y "Completar brief". Marca en ámbar lo que falta; no bloquea guardar (una
// solicitud incompleta se guarda como Incompleta y no se asigna).
import * as React from "react";
import { Plus, X } from "lucide-react";
import {
  Boton,
  Campo,
  Input,
  Select,
  SelectContenido,
  SelectDisparador,
  SelectOpcion,
  SelectValor,
  Textarea,
} from "@iavmx/ui";
import type { Catalogo } from "./consultas";
import { Chip, Falta } from "./componentes";

export interface EntregableBrief {
  nombre: string;
  tipo: string;
  area: string | null;
}

export interface Brief {
  titulo: string;
  objetivo: string | null;
  publico: string | null;
  productos: string[];
  fechaPublicacion: string | null;
  areaSolicitante: string | null;
  solicitanteId: string | null;
  entregables: EntregableBrief[];
  presupuesto: number | null;
  prioridad: "baja" | "normal" | "alta" | "urgente";
  materialesDelSolicitante: string[];
}

export const BRIEF_VACIO: Brief = {
  titulo: "",
  objetivo: null,
  publico: null,
  productos: [],
  fechaPublicacion: null,
  areaSolicitante: null,
  solicitanteId: null,
  entregables: [],
  presupuesto: null,
  prioridad: "normal",
  materialesDelSolicitante: [],
};

/** Espejo de la regla del Worker: objetivo, fecha, producto, área y entregables. */
export function faltantesDe(b: Brief): string[] {
  const f: string[] = [];
  if (!b.objetivo?.trim()) f.push("objetivo");
  if (!b.fechaPublicacion) f.push("fechaPublicacion");
  if (b.productos.length === 0) f.push("productos");
  if (!b.areaSolicitante) f.push("areaSolicitante");
  if (b.entregables.length === 0) f.push("entregables");
  return f;
}

const AREA_POR_TIPO: Record<string, string> = {
  video: "multimedia",
  foto: "multimedia",
  capsula: "multimedia",
  reel: "multimedia",
  arte: "multimedia",
  entrevista: "editorial",
  nota: "editorial",
  impreso: "medio-pliego",
  landing: "estrategia-digital",
  formulario: "estrategia-digital",
  qr: "estrategia-digital",
  copy: "mercadotecnia",
  presentacion: "mercadotecnia",
  otro: "mercadotecnia",
};

export function FormularioBrief({
  valor,
  onChange,
  catalogo,
  mostrarFaltantes = true,
  verPresupuesto = true,
  idBase = "brief",
}: {
  valor: Brief;
  onChange: (b: Brief) => void;
  catalogo: Catalogo;
  mostrarFaltantes?: boolean;
  verPresupuesto?: boolean;
  idBase?: string;
}) {
  const faltan = mostrarFaltantes ? faltantesDe(valor) : [];
  const falta = (c: string) => faltan.includes(c);
  const set = <K extends keyof Brief>(k: K, v: Brief[K]) => onChange({ ...valor, [k]: v });
  const personasDelArea = valor.areaSolicitante
    ? catalogo.personas.filter((p) => p.areaId === catalogo.areas.find((a) => a.clave === valor.areaSolicitante)?.id)
    : catalogo.personas;
  const [material, setMaterial] = React.useState("");
  const agregarMaterial = () => {
    if (!material.trim()) return;
    set("materialesDelSolicitante", [...valor.materialesDelSolicitante, material.trim()]);
    setMaterial("");
  };

  return (
    <div className="grid gap-5">
      <Campo etiqueta="Título de la campaña" htmlFor={`${idBase}-titulo`}>
        <Input
          id={`${idBase}-titulo`}
          value={valor.titulo}
          onChange={(e) => set("titulo", e.target.value)}
          placeholder="Aniversario Vanguardia · 4 de octubre"
          className="text-base"
          required
        />
      </Campo>

      <div className="grid gap-5 sm:grid-cols-2">
        <Campo etiqueta="Área solicitante" htmlFor={`${idBase}-area`}>
          <div className="flex items-center gap-2">
            <Select value={valor.areaSolicitante ?? ""} onValueChange={(v) => onChange({ ...valor, areaSolicitante: v || null, solicitanteId: null })}>
              <SelectDisparador id={`${idBase}-area`} aria-label="Área solicitante" className="text-base">
                <SelectValor placeholder="Elige el área" />
              </SelectDisparador>
              <SelectContenido>
                {catalogo.areas.map((a) => (
                  <SelectOpcion key={a.id} value={a.clave}>
                    {a.nombre}
                  </SelectOpcion>
                ))}
              </SelectContenido>
            </Select>
            {falta("areaSolicitante") && <Falta />}
          </div>
        </Campo>
        <Campo etiqueta="Quién lo pide" htmlFor={`${idBase}-solicitante`} ayuda="Opcional. Recibe el aviso si falta algo.">
          <Select value={valor.solicitanteId ?? ""} onValueChange={(v) => set("solicitanteId", v || null)}>
            <SelectDisparador id={`${idBase}-solicitante`} aria-label="Quién lo pide" className="text-base">
              <SelectValor placeholder="Persona" />
            </SelectDisparador>
            <SelectContenido>
              {personasDelArea.map((p) => (
                <SelectOpcion key={p.id} value={p.id}>
                  {p.nombre}
                </SelectOpcion>
              ))}
            </SelectContenido>
          </Select>
        </Campo>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Campo etiqueta="Fecha de publicación" htmlFor={`${idBase}-fecha`} ayuda="Toda fecha límite de las tareas irá antes de este día.">
          <div className="flex items-center gap-2">
            <Input
              id={`${idBase}-fecha`}
              type="date"
              value={valor.fechaPublicacion ?? ""}
              onChange={(e) => set("fechaPublicacion", e.target.value || null)}
              className="text-base"
            />
            {falta("fechaPublicacion") && <Falta />}
          </div>
        </Campo>
        <Campo etiqueta="Prioridad" htmlFor={`${idBase}-prioridad`}>
          <Select value={valor.prioridad} onValueChange={(v) => set("prioridad", v as Brief["prioridad"])}>
            <SelectDisparador id={`${idBase}-prioridad`} aria-label="Prioridad" className="text-base">
              <SelectValor />
            </SelectDisparador>
            <SelectContenido>
              <SelectOpcion value="baja">Baja</SelectOpcion>
              <SelectOpcion value="normal">Normal</SelectOpcion>
              <SelectOpcion value="alta">Alta</SelectOpcion>
              <SelectOpcion value="urgente">Urgente (Mercadotecnia la evalúa a mano)</SelectOpcion>
            </SelectContenido>
          </Select>
        </Campo>
      </div>

      <Campo etiqueta="Objetivo" htmlFor={`${idBase}-objetivo`}>
        <div className="flex items-start gap-2">
          <Textarea
            id={`${idBase}-objetivo`}
            value={valor.objetivo ?? ""}
            onChange={(e) => set("objetivo", e.target.value || null)}
            placeholder="Qué debe lograr la campaña, en una o dos líneas."
            className="min-h-20 text-base"
          />
          {falta("objetivo") && <Falta />}
        </div>
      </Campo>

      <div>
        <div className="mb-1.5 flex items-center gap-2">
          <span className="font-ui text-xs font-semibold uppercase tracking-wide text-tinta-3">Producto</span>
          {falta("productos") && <Falta />}
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Productos">
          {catalogo.productos.map((p) => {
            const activo = valor.productos.includes(p);
            return (
              <Chip key={p} activo={activo} onClick={() => set("productos", activo ? valor.productos.filter((x) => x !== p) : [...valor.productos, p])}>
                {p}
              </Chip>
            );
          })}
        </div>
      </div>

      <Campo etiqueta="Público" htmlFor={`${idBase}-publico`} ayuda="A quién le hablamos. No es obligatorio para asignar.">
        <Input id={`${idBase}-publico`} value={valor.publico ?? ""} onChange={(e) => set("publico", e.target.value || null)} placeholder="Anunciantes de Saltillo, suscriptores, público general…" className="text-base" />
      </Campo>

      <div>
        <div className="mb-1.5 flex items-center gap-2">
          <span className="font-ui text-xs font-semibold uppercase tracking-wide text-tinta-3">Entregables</span>
          {falta("entregables") && <Falta />}
        </div>
        <ul className="grid gap-2">
          {valor.entregables.map((e, i) => (
            <li key={i} className="grid gap-2 border border-linea p-2 sm:grid-cols-[1fr_150px_170px_auto] sm:items-center">
              <Input aria-label={`Nombre del entregable ${i + 1}`} value={e.nombre} onChange={(ev) => set("entregables", valor.entregables.map((x, j) => (j === i ? { ...x, nombre: ev.target.value } : x)))} className="text-base" />
              <Select value={e.tipo} onValueChange={(v) => set("entregables", valor.entregables.map((x, j) => (j === i ? { ...x, tipo: v, area: x.area ?? AREA_POR_TIPO[v] ?? null } : x)))}>
                <SelectDisparador aria-label="Tipo de entregable" className="text-base">
                  <SelectValor />
                </SelectDisparador>
                <SelectContenido>
                  {catalogo.tiposEntregable.map((t) => (
                    <SelectOpcion key={t.clave} value={t.clave}>
                      {t.nombre}
                    </SelectOpcion>
                  ))}
                </SelectContenido>
              </Select>
              <Select value={e.area ?? ""} onValueChange={(v) => set("entregables", valor.entregables.map((x, j) => (j === i ? { ...x, area: v || null } : x)))}>
                <SelectDisparador aria-label="Área que lo produce" className="text-base">
                  <SelectValor placeholder="Área que lo produce" />
                </SelectDisparador>
                <SelectContenido>
                  {catalogo.areas.map((a) => (
                    <SelectOpcion key={a.id} value={a.clave}>
                      {a.nombre}
                    </SelectOpcion>
                  ))}
                </SelectContenido>
              </Select>
              <Boton type="button" variante="fantasma" tamano="icono" aria-label={`Quitar ${e.nombre || "entregable"}`} onClick={() => set("entregables", valor.entregables.filter((_, j) => j !== i))}>
                <X />
              </Boton>
            </li>
          ))}
        </ul>
        <Boton type="button" variante="contorno" tamano="sm" className="mt-2 h-11 md:h-8" onClick={() => set("entregables", [...valor.entregables, { nombre: "", tipo: "arte", area: "multimedia" }])}>
          <Plus /> Agregar entregable
        </Boton>
      </div>

      <div>
        <span className="font-ui text-xs font-semibold uppercase tracking-wide text-tinta-3">Materiales que debe dar el solicitante</span>
        <p className="mt-0.5 font-ui text-xs text-tinta-tenue">Logotipos, precios, autorizaciones, listas. Se piden con la solicitud.</p>
        <ul className="mt-2 flex flex-wrap gap-2">
          {valor.materialesDelSolicitante.map((m, i) => (
            <li key={i} className="inline-flex items-center gap-1 border border-linea px-2 py-1 font-ui text-sm">
              {m}
              <button type="button" aria-label={`Quitar ${m}`} className="opacity-60 hover:opacity-100" onClick={() => set("materialesDelSolicitante", valor.materialesDelSolicitante.filter((_, j) => j !== i))}>
                <X className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
        {/* No es un <form>: este bloque vive dentro del formulario de la solicitud y un form anidado lo mandaría entero. */}
        <div className="mt-2 flex gap-2">
          <Input
            aria-label="Material pendiente"
            value={material}
            onChange={(e) => setMaterial(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              agregarMaterial();
            }}
            placeholder="Logotipos de los clientes"
            className="max-w-xs text-base"
          />
          <Boton type="button" variante="contorno" tamano="md" onClick={agregarMaterial} disabled={!material.trim()}>
            Agregar
          </Boton>
        </div>
      </div>

      {verPresupuesto && (
        <Campo etiqueta="Presupuesto (opcional)" htmlFor={`${idBase}-presupuesto`} ayuda="Visible solo para Mercadotecnia y Dirección.">
          <Input
            id={`${idBase}-presupuesto`}
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={valor.presupuesto ?? ""}
            onChange={(e) => set("presupuesto", e.target.value === "" ? null : Number(e.target.value))}
            placeholder="MXN"
            className="max-w-xs text-base"
          />
        </Campo>
      )}
    </div>
  );
}
